import { Suspense } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { MembershipProvider } from "@/contexts/MembershipContext";
import { AnimatePresence } from "framer-motion";
import ScrollToTop from "@/components/ScrollToTop";
import BadgeNotificationListener from "@/components/BadgeNotificationListener";
import ActivityTracker from "@/components/ActivityTracker";
import PageLoader from "@/components/PageLoader";
import PageTransition from "@/components/PageTransition";
import AdminRedirect from "@/components/AdminRedirect";
import { HexagonTransitionOverlay } from "@/components/HexagonTransition";

// Lazy load pages for code splitting
const Index = lazy(() => import("./pages/Index"));
const VisionGallery = lazy(() => import("./pages/VisionGallery"));
const VisionDetail = lazy(() => import("./pages/VisionDetail"));
const ServiceGallery = lazy(() => import("./pages/ServiceGallery"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const Roadmap = lazy(() => import("./pages/Roadmap"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Settings = lazy(() => import("./pages/Settings"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const About = lazy(() => import("./pages/About"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const MilestoneNotesPage = lazy(() => import("./pages/MilestoneNotesPage"));
const NotesSummary = lazy(() => import("./pages/NotesSummary"));
const AdminCRM = lazy(() => import("./pages/AdminCRM"));
const AdminSEO = lazy(() => import("./pages/AdminSEO"));
const VoteHistory = lazy(() => import("./pages/VoteHistory"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Donate = lazy(() => import("./pages/Donate"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Legal = lazy(() => import("./pages/Legal"));
const HiveFund = lazy(() => import("./pages/HiveFund"));
const SiteMapPage = lazy(() => import("./pages/SiteMap"));
const BrandGuide = lazy(() => import("./pages/BrandGuide"));
const Discover = lazy(() => import("./pages/Discover"));
const BlogStartupMentor = lazy(() => import("./pages/BlogStartupMentor"));
const BlogStartupHelp = lazy(() => import("./pages/BlogStartupHelp"));
const BlogIncubatorVsAccelerator = lazy(() => import("./pages/BlogIncubatorVsAccelerator"));
const BlogBusinessAdvisor = lazy(() => import("./pages/BlogBusinessAdvisor"));
const BlogHowToStartBusiness = lazy(() => import("./pages/BlogHowToStartBusiness"));
const BlogValidateStartupIdea = lazy(() => import("./pages/BlogValidateStartupIdea"));
const BlogNetworkingSites = lazy(() => import("./pages/BlogNetworkingSites"));
const BlogIndex = lazy(() => import("./pages/BlogIndex"));
const JoinCard = lazy(() => import("./pages/JoinCard"));
const Collectables = lazy(() => import("./pages/Collectables"));
const Events = lazy(() => import("./pages/Events"));
const Groups = lazy(() => import("./pages/Groups"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const SubmitVision = lazy(() => import("./pages/SubmitVision"));
const SubmitService = lazy(() => import("./pages/SubmitService"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Animated Routes component
const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="sync">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><AdminRedirect><Index /></AdminRedirect></PageTransition>} />
        <Route path="/visions" element={<PageTransition><VisionGallery /></PageTransition>} />
        <Route path="/visions/:id" element={<PageTransition><VisionDetail /></PageTransition>} />
        <Route path="/services" element={<PageTransition><ServiceGallery /></PageTransition>} />
        <Route path="/services/:id" element={<PageTransition><ServiceDetail /></PageTransition>} />
        <Route path="/roadmap" element={<PageTransition><Roadmap /></PageTransition>} />
        <Route path="/dashboard" element={<PageTransition><AdminRedirect><Dashboard /></AdminRedirect></PageTransition>} />
        <Route path="/settings" element={<PageTransition><AdminRedirect><Settings /></AdminRedirect></PageTransition>} />
        <Route path="/leaderboard" element={<PageTransition><Leaderboard /></PageTransition>} />
        <Route path="/about" element={<PageTransition><About /></PageTransition>} />
        <Route path="/reset-password" element={<PageTransition><ResetPassword /></PageTransition>} />
        <Route path="/milestone-notes/:milestoneId" element={<PageTransition><AdminRedirect><MilestoneNotesPage /></AdminRedirect></PageTransition>} />
        <Route path="/notes-summary/:visionId" element={<PageTransition><AdminRedirect><NotesSummary /></AdminRedirect></PageTransition>} />
        <Route path="/admin" element={<PageTransition><AdminCRM /></PageTransition>} />
        <Route path="/admin/seo" element={<PageTransition><AdminSEO /></PageTransition>} />
        <Route path="/vote-history" element={<PageTransition><AdminRedirect><VoteHistory /></AdminRedirect></PageTransition>} />
        <Route path="/pricing" element={<PageTransition><Pricing /></PageTransition>} />
        <Route path="/donate" element={<PageTransition><Donate /></PageTransition>} />
        <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
        <Route path="/faq" element={<PageTransition><FAQ /></PageTransition>} />
        <Route path="/legal" element={<PageTransition><Legal /></PageTransition>} />
        <Route path="/hive-fund" element={<PageTransition><HiveFund /></PageTransition>} />
        <Route path="/sitemap" element={<PageTransition><SiteMapPage /></PageTransition>} />
        <Route path="/brandguide" element={<PageTransition><BrandGuide /></PageTransition>} />
        <Route path="/discover" element={<PageTransition><Discover /></PageTransition>} />
        <Route path="/blog" element={<PageTransition><BlogIndex /></PageTransition>} />
        <Route path="/blog/how-to-find-a-startup-mentor" element={<PageTransition><BlogStartupMentor /></PageTransition>} />
        <Route path="/blog/startup-help" element={<PageTransition><BlogStartupHelp /></PageTransition>} />
        <Route path="/blog/startup-incubator-vs-accelerator" element={<PageTransition><BlogIncubatorVsAccelerator /></PageTransition>} />
        <Route path="/blog/business-advisor" element={<PageTransition><BlogBusinessAdvisor /></PageTransition>} />
        <Route path="/blog/how-to-start-a-business" element={<PageTransition><BlogHowToStartBusiness /></PageTransition>} />
        <Route path="/blog/how-to-validate-a-startup-idea" element={<PageTransition><BlogValidateStartupIdea /></PageTransition>} />
        <Route path="/blog/best-networking-sites-for-entrepreneurs" element={<PageTransition><BlogNetworkingSites /></PageTransition>} />
        <Route path="/join/:code" element={<PageTransition><JoinCard /></PageTransition>} />
        <Route path="/collectables" element={<PageTransition><AdminRedirect><Collectables /></AdminRedirect></PageTransition>} />
        <Route path="/events" element={<PageTransition><Events /></PageTransition>} />
        <Route path="/groups" element={<PageTransition><Groups /></PageTransition>} />
        <Route path="/unsubscribe" element={<PageTransition><Unsubscribe /></PageTransition>} />
        <Route path="/submit-vision" element={<PageTransition><SubmitVision /></PageTransition>} />
        <Route path="/submit-service" element={<PageTransition><SubmitService /></PageTransition>} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
      </Routes>
    </AnimatePresence>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <MembershipProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <BadgeNotificationListener />
            <ActivityTracker />
            <HexagonTransitionOverlay />
            <Suspense fallback={<PageLoader />}>
              <AnimatedRoutes />
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </MembershipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
